import { CardShell, Field, SectionCard, SelectField } from '@atlas/ui-components'
import { Button, cn, Spinner } from '@atlas/ui-primitives'
import { Palette } from 'lucide-react'
import type { FormEvent } from 'react'
import { ArticlePaletteNode } from '@/components/articles/palette-extension'
import { ClassifyPicker } from '@/components/classification/ClassifyPicker'
import { RichTextEditor, type RichTextEditorHandle } from '@/components/ui/RichTextEditor'
import { ARTICLE_STATUS_LABELS, ARTICLE_TYPE_LABELS, type ArticleStatus, ArticleType } from '@/domain/article.types'
import type { RailAxis } from '@/hooks/useLibrary'
import { type ArticleFormState, canSubmit } from './form-model'

/**
 * Images are added by dropping them into the body, so all the composer needs is the
 * editor handle its caller placed images through and a way to report a drop.
 *
 * There is no image panel: the placement IS the import, and a second surface listing
 * the same files was a list of things the author had already put where they wanted.
 */
export interface ArticleComposerImageProps {
	/**
	 * Handle on the body editor, owned by the caller because the caller is what
	 * places a dropped image and later swaps its preview for the stored URL.
	 */
	editorRef: React.RefObject<RichTextEditorHandle | null>
	/** An image drop on the canvas, with the position it landed on. */
	onDropFiles: (files: File[], position: number) => void
	/** An image is still uploading, so the body is not yet safe to save. */
	isUploading: boolean
}

export interface ArticleComposerProps extends ArticleComposerImageProps {
	form: ArticleFormState
	set: <K extends keyof ArticleFormState>(key: K, value: ArticleFormState[K]) => void
	/** Registry axes for the classification picker (from `useAxesWithValues`). */
	axes?: RailAxis[]

	/**
	 * The transitions the API says this caller may make, verbatim. EMPTY on create,
	 * where the article does not exist yet and has no transitions.
	 *
	 * Never derived here from the status and the user's role: `USER` may archive but
	 * only `ADMIN` may publish, and a second copy of that rule would disagree with
	 * the API's silently.
	 */
	availableActions?: readonly ArticleStatus[]
	onTransition?: (to: ArticleStatus) => void
	isTransitioning?: boolean

	submitLabel: string
	isPending: boolean
	onSubmit: () => void
	onCancel: () => void
	/** A failed save or transition, surfaced without clearing the form. */
	error?: string | null
}

const TYPE_OPTIONS = Object.values(ArticleType).map((type) => ({ value: type, label: ARTICLE_TYPE_LABELS[type] }))

/**
 * Module-level so the array is referentially stable: a fresh one each render would
 * rebuild the editor and drop the cursor mid-sentence.
 */
const PALETTE_EXTENSIONS = [ArticlePaletteNode]

export const TOOLBAR_ICON_CLASS = 'h-3.5 w-3.5'

/** Matches the shared editor toolbar's own buttons, so the extra control is not a stranger. */
function ToolbarButton({
	label,
	onClick,
	children,
}: {
	label: string
	onClick: () => void
	children: React.ReactNode
}) {
	return (
		<button
			type="button"
			aria-label={label}
			onMouseDown={(event) => event.preventDefault()}
			onClick={onClick}
			className={cn(
				'flex h-7 w-7 items-center justify-center rounded-ds-sm text-ds-text-muted hover:bg-ds-surface hover:text-ds-text',
			)}
		>
			{children}
		</button>
	)
}

/** One titled block in the composer's right-hand column. */
function SidePanel({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<SectionCard title={title} className="p-4">
			{children}
		</SectionCard>
	)
}

/**
 * The transitions the API offered, as buttons.
 *
 * Renders nothing when there are none — which is the create surface, and also an
 * article whose caller may not move it. Both are correctly silent rather than
 * showing a disabled control the user cannot explain.
 */
function StatusActions({
	actions,
	onTransition,
	isTransitioning,
}: {
	actions: readonly ArticleStatus[]
	onTransition: (to: ArticleStatus) => void
	isTransitioning: boolean
}) {
	if (actions.length === 0) return null
	return (
		<SidePanel title="Status">
			<div className="flex flex-wrap gap-2">
				{actions.map((action) => (
					<Button
						key={action}
						type="button"
						variant="secondary"
						size="sm"
						className="rounded-ds-full"
						isDisabled={isTransitioning}
						onPress={() => onTransition(action)}
					>
						Move to {ARTICLE_STATUS_LABELS[action].toLowerCase()}
					</Button>
				))}
			</div>
		</SidePanel>
	)
}

/** The composer's right-hand column: what the article IS, rather than what it says. */
function ComposerSidebar({
	form,
	set,
	axes,
	availableActions,
	onTransition,
	isTransitioning,
}: {
	form: ArticleFormState
	set: ArticleComposerProps['set']
	axes: RailAxis[]
	availableActions: readonly ArticleStatus[]
	onTransition?: (to: ArticleStatus) => void
	isTransitioning: boolean
}) {
	return (
		<aside className="space-y-4">
			<SidePanel title="Type">
				<SelectField
					label=""
					ariaLabel="Article type"
					value={form.articleType}
					options={TYPE_OPTIONS}
					isClearable={false}
					onChange={(value) => set('articleType', value as ArticleType)}
				/>
			</SidePanel>

			<SidePanel title="Classification">
				<ClassifyPicker
					value={form.classification}
					axes={axes}
					onChange={(next) => set('classification', next)}
					emptyLabel="No axes attached — add one to classify this article."
				/>
			</SidePanel>

			{onTransition ? (
				<StatusActions actions={availableActions} onTransition={onTransition} isTransitioning={isTransitioning} />
			) : null}
		</aside>
	)
}

/**
 * The article composer — one form for both creating and editing, prop-driven with
 * no fetching and no routing, so the two pages own the data and this owns the shape.
 *
 * Deliberately narrow: a title, a summary, the type, the body, its classification
 * and its images. No cover-image slot and no locale control — authoring a
 * translation is the translation flow's job, and the locale a source article is
 * written in is not a per-article decision.
 */
export function ArticleComposer({
	form,
	set,
	axes = [],
	availableActions = [],
	onTransition,
	isTransitioning = false,
	submitLabel,
	isPending,
	onSubmit,
	onCancel,
	error,
	editorRef,
	onDropFiles,
	isUploading,
}: ArticleComposerProps) {
	const submit = (event: FormEvent) => {
		event.preventDefault()
		onSubmit()
	}

	return (
		<form onSubmit={submit}>
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
				{/*
				 * One elevated sheet for the whole authoring column, on the app's grey page
				 * surface: the fields and the canvas read as one document rather than as
				 * controls floating on the background, which is also what makes a bordered
				 * input legible - it sits on white, not on grey.
				 */}
				<CardShell as="section" className="space-y-5 p-6">
					{/*
					 * Title and summary are ordinary labelled form fields, not part of the writing
					 * canvas: they are their own API fields - `title` is required and drives the
					 * slug, and both are what search matches - so the form says so rather than
					 * dressing them as the document's first lines. Only the body is authored content.
					 */}
					<Field
						label="Title"
						value={form.title}
						onChange={(event) => set('title', event.target.value)}
						placeholder="Untitled"
					/>
					<Field
						label="Summary"
						value={form.summary}
						onChange={(event) => set('summary', event.target.value)}
						placeholder="A short excerpt"
						description="Shown in the article list, and matched by search alongside the title."
					/>
					<RichTextEditor
						ref={editorRef}
						value={form.content}
						onChange={(html) => set('content', html)}
						placeholder="Write, or start from a template…"
						aria-label="Article body"
						variant="canvas"
						extensions={PALETTE_EXTENSIONS}
						onDropFiles={onDropFiles}
						renderToolbarExtra={(editor) => (
							<ToolbarButton
								label="Insert colour palettes"
								onClick={() => editor.chain().focus().insertArticlePalette().run()}
							>
								<Palette className={TOOLBAR_ICON_CLASS} />
							</ToolbarButton>
						)}
					/>
					{/* Said once, quietly, because the gesture is otherwise undiscoverable:
					    there is no import button to notice. */}
					<p className="text-xs text-ds-text-subtle">
						Drop an image into the body to place it where you drop it. It uploads while you keep writing.
					</p>
				</CardShell>

				<ComposerSidebar
					form={form}
					set={set}
					axes={axes}
					availableActions={availableActions}
					onTransition={onTransition}
					isTransitioning={isTransitioning}
				/>
			</div>

			{error ? (
				<p role="alert" className="mt-6 text-sm text-ds-danger-strong">
					{error}
				</p>
			) : null}

			<div className="mt-6 flex items-center gap-3">
				{/*
				 * Held while an image is uploading: the body still carries that image's
				 * local preview, and saving would store a `blob:` URL that resolves in
				 * this tab and nowhere else.
				 */}
				<Button
					type="submit"
					isDisabled={!canSubmit(form) || isPending || isUploading}
					className="rounded-ds-full px-6"
				>
					{isPending ? 'Saving…' : submitLabel}
				</Button>
				<Button type="button" variant="secondary" className="rounded-ds-full" onPress={onCancel}>
					Cancel
				</Button>
				{isUploading ? <Spinner size="sm" label="Finishing an image upload" /> : null}
			</div>
		</form>
	)
}
